<template>
  <div class="container p-4">
    <div class="flex flex-wrap align-items-center gap-2 mb-3">
      <ButtonPrime icon="pi pi-arrow-left" class="p-button-text" @click="router.push({ name: 'estudyoo-hub' })" />
      <h3 class="text-xl font-bold m-0">Actividades Estudyoo</h3>
    </div>

    <DjangoAuthGate @authenticated="onDjangoAuth" />

    <template v-if="djangoOk">
      <section class="act-overview surface-card border-round p-3 mb-3 border-1 surface-border">
        <div class="flex flex-wrap align-items-center gap-2 mb-2">
          <h4 class="text-base font-semibold m-0 flex-1">Estado del catálogo</h4>
          <ButtonPrime
            icon="pi pi-refresh"
            label="Actualizar"
            class="p-button-sm p-button-outlined"
            :loading="catalogLoading"
            @click="bootstrapCatalog(true)"
          />
          <ButtonPrime
            icon="pi pi-sitemap"
            label="Mapa capítulos"
            class="p-button-sm p-button-text"
            @click="router.push({ name: 'estudyoo-mapa-capitulos' })"
          />
        </div>
        <div v-if="catalogLoading && !resumenPanel" class="text-sm text-500 py-2">
          <i class="pi pi-spin pi-spinner mr-2" />Cargando catálogo base…
        </div>
        <div v-else-if="resumenPanel" class="act-overview__grid">
          <div v-for="card in overviewCards" :key="card.key" class="act-overview__stat">
            <span class="act-overview__stat-value" :class="card.warn ? 'text-orange-600' : ''">{{ card.value }}</span>
            <span class="act-overview__stat-label">{{ card.label }}</span>
          </div>
        </div>
        <div v-if="overviewAlerts.length" class="mt-2 flex flex-column gap-1">
          <p v-for="(msg, i) in overviewAlerts" :key="i" class="text-xs m-0 p-2 border-round" :class="msg.severity">
            <i :class="`pi ${msg.icon} mr-1`" />{{ msg.text }}
          </p>
        </div>
        <p v-if="catalogReady" class="text-xs text-500 m-0 mt-2">
          {{ materias.length }} materia(s) · {{ cursos.length }} curso(s) · catálogo listo para filtrar
        </p>
      </section>

      <div class="act-ia-banner surface-card border-round p-3 mb-3 flex flex-wrap align-items-center gap-2">
        <i class="pi pi-sparkles text-violet-500" />
        <span class="text-sm flex-1">
          <strong>Generación con IA</strong> — capítulo + tipo de actividad, import directo al catálogo.
        </span>
        <ButtonPrime
          label="Ir a sección IA"
          icon="pi pi-arrow-right"
          class="p-button-sm p-button-outlined"
          @click="router.push({ name: 'estudyoo-actividades-ia' })"
        />
      </div>
      <TabView v-model:activeIndex="activeTabIndex" lazy @tab-change="onTabChange">
        <TabPanel header="Actividades">
          <p class="text-sm text-600 mb-3">
            Listado de actividades creadas. Para <strong>crear manualmente</strong> (con imágenes y asignación al capítulo),
            usa la pestaña <strong>Creación manual</strong> o el botón de abajo.
          </p>
          <div class="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3 p-3 border-round surface-border border-1">
            <div>
              <label class="block text-sm mb-1">Materia</label>
              <Dropdown
                :key="`mat-${materias.length}`"
                v-model="filtroMateria"
                :options="materias"
                option-label="nombre"
                placeholder="Elegir materia"
                class="w-full"
                filter
                show-clear
                :loading="catalogLoading"
                @change="onFiltroMateriaActChange"
              />
            </div>
            <div>
              <label class="block text-sm mb-1">Nivel</label>
              <Dropdown
                v-model="filtroNivel"
                :options="nivelOptsActividades"
                option-label="label"
                option-value="value"
                placeholder="Elegir nivel"
                class="w-full"
                filter
                show-clear
                @change="onFiltroNivelActChange"
              />
            </div>
            <div>
              <label class="block text-sm mb-1">Curso</label>
              <Dropdown
                :key="`cur-${filtroNivel}-${cursoOptsActividades.length}`"
                v-model="filtroCurso"
                :options="cursoOptsActividades"
                option-label="label"
                :placeholder="filtroNivel ? 'Sección A, B…' : 'Todos los cursos'"
                class="w-full"
                filter
                show-clear
                @change="onFiltroCursoChange"
              />
            </div>
            <div>
              <label class="block text-sm mb-1">Capítulo</label>
              <Dropdown
                :key="`cap-${capitulosActTab.length}`"
                v-model="filtroCapitulo"
                :options="capituloOptsActTab"
                option-label="label"
                :placeholder="capituloActPlaceholder"
                class="w-full"
                filter
                show-clear
                :disabled="!filtroMateria"
                :loading="loadingCapitulosAct"
              />
              <p v-if="filtroMateria && filtroNivel" class="text-xs text-500 mt-1 mb-0">
                {{ capitulosActTab.length }} capítulo(s) en esta materia y nivel
              </p>
            </div>
            <div class="flex flex-wrap align-items-end gap-2">
              <ButtonPrime label="Buscar" icon="pi pi-search" :loading="loading" @click="aplicarFiltroActividades" />
              <ButtonPrime label="Limpiar" icon="pi pi-filter-slash" class="p-button-outlined" @click="limpiarFiltroActividades" />
            </div>
          </div>
          <EstudyooLoadingBanner
            :visible="importando || descargandoPlantilla"
            :message="importando ? 'Importando Excel… puede tardar un momento.' : 'Descargando plantilla…'"
          />
          <div class="flex flex-wrap gap-2 mb-3">
            <ButtonPrime label="Nueva actividad manual" icon="pi pi-plus" @click="irTabManual" />
            <ButtonPrime label="Recargar catálogo" icon="pi pi-refresh" class="p-button-outlined" :loading="catalogLoading" @click="bootstrapCatalog(true)" />
            <ButtonPrime label="Plantilla Excel" icon="pi pi-download" class="p-button-outlined" :loading="descargandoPlantilla" :disabled="importando" @click="bajarPlantilla" />
            <FileUpload
              mode="basic"
              choose-label="Importar Excel"
              accept=".xlsx,.xls"
              :auto="true"
              custom-upload
              :disabled="importando || descargandoPlantilla"
              @uploader="importar"
            />
          </div>
          <div v-if="actividadesBuscadas && actividadStats.total" class="flex flex-wrap gap-3 mb-3">
            <div class="act-mini-stat surface-card border-round p-2 px-3 border-1 surface-border">
              <span class="text-xs text-500">Actividades</span>
              <p class="text-lg font-bold m-0">{{ actividadStats.total }}</p>
            </div>
            <div v-if="filtroCurso" class="act-mini-stat surface-card border-round p-2 px-3 border-1 surface-border">
              <span class="text-xs text-500">Asignadas al curso</span>
              <p class="text-lg font-bold m-0 text-green-600">{{ actividadStats.asignadas }}</p>
            </div>
            <div v-if="filtroCurso && actividadStats.pendientes" class="act-mini-stat surface-card border-round p-2 px-3 border-1 surface-border">
              <span class="text-xs text-500">Sin asignar</span>
              <p class="text-lg font-bold m-0 text-orange-600">{{ actividadStats.pendientes }}</p>
            </div>
          </div>
          <p v-if="!actividadesBuscadas && !loading" class="text-sm text-500 mb-3">
            Cargando actividades…
          </p>
          <p v-else-if="actividadesBuscadas && !actividades.length && !loading" class="text-sm text-500 mb-3">
            No hay actividades todavía. Usa la pestaña <strong>Creación manual</strong>, importa Excel o la sección IA.
          </p>
          <div v-for="grupo in actividadesAgrupadasPorCapitulo" :key="grupo.capitulo.id" class="mb-4">
            <div class="flex flex-wrap align-items-center gap-2 mb-2">
              <h4 class="text-sm font-semibold m-0">{{ grupo.capituloLabel }}</h4>
              <TagPrime v-if="grupo.cursoLabel" severity="info" :value="grupo.cursoLabel" />
              <span class="text-xs text-500">({{ grupo.actividades.length }} actividad{{ grupo.actividades.length === 1 ? '' : 'es' }})</span>
            </div>
            <DataTable :value="grupo.actividades" :loading="loading" class="p-datatable-sm" data-key="id">
              <Column field="titulo" header="Título" />
              <Column field="subtitulo" header="Subtítulo" />
              <Column header="Tipo">
                <template #body="{ data }">{{ labelTipo(data.tipo_id) }}</template>
              </Column>
              <Column header="Activa">
                <template #body="{ data }">
                  <TagPrime :severity="data.activa !== false ? 'success' : 'secondary'" :value="data.activa !== false ? 'Sí' : 'No'" />
                </template>
              </Column>
              <Column v-if="filtroCurso" header="Asignada al curso">
                <template #body="{ data }">
                  <TagPrime
                    :severity="data.asignada ? 'success' : 'warn'"
                    :value="data.asignada ? 'Sí' : 'No'"
                  />
                </template>
              </Column>
              <Column header="Acciones" style="width: 9rem">
                <template #body="{ data }">
                  <ButtonPrime icon="pi pi-pencil" class="p-button-text p-button-sm" v-tooltip.top="'Editar campos básicos'" @click="abrirEditar('actividad', data)" />
                  <ButtonPrime icon="pi pi-file-edit" class="p-button-text p-button-sm" v-tooltip.top="'Editar preguntas y actividad completa'" @click="editarActividadManual(data)" />
                  <ButtonPrime
                    v-if="filtroCurso && data.asignada"
                    icon="pi pi-times"
                    class="p-button-text p-button-sm p-button-danger"
                    v-tooltip.top="'Quitar asignación del curso'"
                    :loading="quitandoAsigId === data.id"
                    @click="quitarAsignacionActividad(data)"
                  />
                </template>
              </Column>
            </DataTable>
          </div>
          <p v-if="actividadesBuscadas && !actividadesAgrupadasPorCapitulo.length && !loading" class="text-sm text-500">
            No hay actividades para los filtros seleccionados.
          </p>
          <p class="text-xs text-500 mt-2">
            La plantilla Excel incluye hojas <strong>actividades</strong>, <strong>preguntas</strong> y <strong>materiales</strong>,
            con <strong>una fila de ejemplo por cada tipo</strong> (ordenar palabras, emparejar, fracción, etc.).
            Revisa la columna <code>datos_json</code> y la hoja <strong>instrucciones</strong> antes de importar.
          </p>
        </TabPanel>
        <TabPanel header="Creación manual">
          <p class="text-sm text-600 mb-3">
            Crea o edita la actividad completa aquí: <strong>capítulo</strong>, <strong>asignación al curso</strong>,
            cantidad de preguntas <strong>dinámica (1–20)</strong> — igual que con IA —,
            cualquier tipo (ordenar palabras, emparejar, fracción, alternativas…),
            materiales del capítulo y de la actividad.
            Tras guardar el formulario se reinicia para cargar otra seguida (misma materia/capítulo/curso).
            Desde la pestaña Actividades usa el icono <i class="pi pi-file-edit" />
            <strong>Editar preguntas y actividad</strong> para corregir una ya creada (p. ej. post-IA).
          </p>
          <ActividadManualForm
            ref="manualFormRef"
            embedded
            @created="onManualSaved"
            @updated="onManualSaved"
            @unassigned="onManualSaved"
            @imported="onManualImported"
          />
        </TabPanel>
        <TabPanel header="Preguntas">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3 p-3 border-round surface-border border-1">
            <div class="md:col-span-2">
              <label class="block text-sm mb-1">Filtrar por actividad (opcional)</label>
              <Dropdown
                v-model="filtroPregActividad"
                :options="actividadOptsPreguntas"
                option-label="label"
                placeholder="Todas las preguntas"
                class="w-full"
                filter
                show-clear
                @show="cargarActividadesParaFiltro"
              />
            </div>
            <div class="flex flex-wrap align-items-end gap-2">
              <ButtonPrime label="Buscar" icon="pi pi-search" :loading="loadingPreg" @click="cargarPreguntas" />
              <ButtonPrime label="Alternativas" icon="pi pi-list" class="p-button-outlined" :loading="loadingAlt" @click="cargarAlternativas" />
            </div>
          </div>
          <p v-if="!preguntasTabReady" class="text-sm text-500 mb-3">
            Pulsa <strong>Buscar</strong> para cargar preguntas. Para corregir palabras, pares o fracción usa
            <strong>Editar actividad</strong> (abre el formulario completo).
          </p>
          <DataTable :value="preguntas" :loading="loadingPreg" class="p-datatable-sm mb-4" empty-message="Aún no has buscado preguntas">
            <Column field="enunciado" header="Enunciado" />
            <Column field="tipo" header="Tipo" />
            <Column field="actividad_id" header="Actividad ID" />
            <Column field="orden" header="Orden" />
            <Column field="activa" header="Activa" />
            <Column header="Acciones" style="width: 9rem">
              <template #body="{ data }">
                <ButtonPrime
                  icon="pi pi-file-edit"
                  class="p-button-text p-button-sm"
                  v-tooltip.top="'Editar actividad completa (recomendado)'"
                  @click="editarActividadDesdePregunta(data)"
                />
                <ButtonPrime
                  icon="pi pi-pencil"
                  class="p-button-text p-button-sm"
                  v-tooltip.top="'Solo enunciado / tipo / orden'"
                  @click="abrirEditar('pregunta', data)"
                />
              </template>
            </Column>
          </DataTable>
          <h4 v-if="alternativas.length" class="text-sm font-semibold mb-2">Alternativas (imágenes de preguntas)</h4>
          <DataTable v-if="alternativas.length" :value="alternativas" :loading="loadingAlt" class="p-datatable-sm">
            <Column field="texto" header="Texto" />
            <Column header="Imagen">
              <template #body="{ data }">
                <img v-if="data.imagen_url" :src="data.imagen_url" alt="" class="mat-thumb" />
                <span v-else class="text-500 text-xs">{{ data.icono || 'icono' }}</span>
              </template>
            </Column>
            <Column header="Subir imagen">
              <template #body="{ data }">
                <input
                  :ref="(el) => setFileRef('alt', data.id, el)"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  class="hidden"
                  @change="(e) => onSubirImagen(e, 'alternativa', data, 'imagen_url')"
                />
                <ButtonPrime
                  label="Imagen"
                  icon="pi pi-upload"
                  class="p-button-sm p-button-outlined"
                  :loading="uploadingKey === `alt-${data.id}`"
                  @click="triggerFile('alt', data.id)"
                />
              </template>
            </Column>
            <Column header="Editar" style="width: 5rem">
              <template #body="{ data }">
                <ButtonPrime icon="pi pi-pencil" class="p-button-text p-button-sm" @click="abrirEditar('alternativa', data)" />
              </template>
            </Column>
          </DataTable>
        </TabPanel>
        <TabPanel header="Materiales actividad">
          <p class="text-sm text-600 mb-3">
            Materiales vinculados a una <strong>actividad</strong>, dentro de un <strong>capítulo</strong> y nivel de <strong>curso</strong>.
            Elige filtros y pulsa Buscar.
          </p>
          <div class="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3 p-3 border-round surface-border border-1">
            <div>
              <label class="block text-sm mb-1">Curso</label>
              <Dropdown
                v-model="filtroMatActCurso"
                :options="cursoOpts"
                option-label="label"
                placeholder="Todos los cursos"
                class="w-full"
                filter
                show-clear
                @change="onFiltroMatActCursoChange"
              />
            </div>
            <div>
              <label class="block text-sm mb-1">Capítulo</label>
              <Dropdown
                v-model="filtroMatActCapitulo"
                :options="capituloOptsMatAct"
                option-label="label"
                placeholder="Todos los capítulos del nivel"
                class="w-full"
                filter
                show-clear
                @change="onFiltroMatActCapituloChange"
              />
            </div>
            <div>
              <label class="block text-sm mb-1">Actividad</label>
              <Dropdown
                v-model="filtroMatActActividad"
                :options="actividadOptsMatAct"
                option-label="label"
                placeholder="Elegir actividad"
                class="w-full"
                filter
                show-clear
                @show="cargarActividadesParaFiltroMat"
              />
            </div>
            <div class="flex flex-wrap align-items-end gap-2">
              <ButtonPrime label="Buscar" icon="pi pi-search" :loading="loadingMat" @click="cargarMaterialesAct" />
              <ButtonPrime label="Limpiar" icon="pi pi-filter-slash" class="p-button-outlined" @click="limpiarFiltroMaterialesAct" />
            </div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3 p-3 border-round surface-border border-1">
            <div class="md:col-span-2">
              <label class="block text-sm mb-1">Título del material nuevo</label>
              <InputText
                v-model="nuevoMatActTitulo"
                class="w-full"
                placeholder="Ej: Guía de la actividad"
                :disabled="!filtroMatActActividad"
              />
            </div>
            <div class="flex align-items-end gap-2">
              <input
                ref="nuevoMatActFile"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                class="hidden"
                @change="crearMaterialActividadConImagen"
              />
              <ButtonPrime
                label="Crear + subir imagen"
                icon="pi pi-plus"
                :loading="creandoMatAct"
                :disabled="!filtroMatActActividad"
                @click="nuevoMatActFile?.click()"
              />
            </div>
          </div>
          <p class="text-xs text-500 mb-3">
            Para crear material, elige una <strong>actividad</strong> en los filtros de arriba.
          </p>
          <EstudyooLoadingBanner
            :visible="importandoMat || descargandoPlantillaMat"
            :message="importandoMat ? 'Importando materiales…' : 'Descargando plantilla…'"
          />
          <div class="flex flex-wrap gap-2 mb-3">
            <ButtonPrime label="Plantilla materiales" icon="pi pi-download" class="p-button-outlined" :loading="descargandoPlantillaMat" @click="bajarPlantillaMateriales" />
            <FileUpload
              mode="basic"
              choose-label="Importar materiales Excel"
              accept=".xlsx,.xls"
              :auto="true"
              custom-upload
              :disabled="importandoMat"
              @uploader="importarMaterialesExcel"
            />
          </div>
          <p v-if="!materialesActTabReady" class="text-sm text-500 mb-3">
            Pulsa <strong>Buscar</strong> para ver materiales (opcional: filtra por curso, capítulo o actividad).
          </p>
          <DataTable :value="materialesAct" :loading="loadingMat" class="p-datatable-sm" empty-message="Sin materiales para los filtros">
            <Column field="titulo" header="Título" />
            <Column field="tipo" header="Tipo" />
            <Column header="Curso">
              <template #body="{ data }">{{ labelCursoMaterial(data) }}</template>
            </Column>
            <Column header="Capítulo">
              <template #body="{ data }">{{ labelCapituloMaterial(data) }}</template>
            </Column>
            <Column header="Actividad">
              <template #body="{ data }">{{ labelActividadMaterial(data) }}</template>
            </Column>
            <Column header="Vista previa">
              <template #body="{ data }">
                <img v-if="data.url" :src="data.url" alt="" class="mat-thumb" />
                <span v-else class="text-500 text-xs">Sin imagen</span>
              </template>
            </Column>
            <Column field="url" header="URL">
              <template #body="{ data }">
                <a v-if="data.url" :href="data.url" target="_blank" rel="noopener" class="text-xs">Ver</a>
                <span v-else>—</span>
              </template>
            </Column>
            <Column header="Subir imagen">
              <template #body="{ data }">
                <input
                  :ref="(el) => setFileRef('act', data.id, el)"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  class="hidden"
                  @change="(e) => onSubirImagen(e, 'material_actividad', data)"
                />
                <ButtonPrime
                  label="Imagen"
                  icon="pi pi-upload"
                  class="p-button-sm p-button-outlined"
                  :loading="uploadingKey === `act-${data.id}`"
                  @click="triggerFile('act', data.id)"
                />
              </template>
            </Column>
            <Column header="Editar" style="width: 5rem">
              <template #body="{ data }">
                <ButtonPrime icon="pi pi-pencil" class="p-button-text p-button-sm" @click="abrirEditar('material_act', data)" />
              </template>
            </Column>
          </DataTable>
        </TabPanel>
        <TabPanel header="Materiales capítulo">
          <p class="text-sm text-600 mb-3">
            Imágenes y recursos del capítulo (guías, diagramas). Elige <strong>materia</strong> y <strong>curso</strong>
            para acotar la lista de capítulos antes de buscar o crear.
          </p>
          <div class="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3 p-3 border-round surface-border border-1">
            <div>
              <label class="block text-sm mb-1">Materia <span class="text-red-500">*</span></label>
              <Dropdown
                v-model="filtroMatCapMateria"
                :options="materias"
                option-label="nombre"
                placeholder="Elegir materia"
                class="w-full"
                filter
                show-clear
                @change="onFiltroMatCapMateriaChange"
              />
            </div>
            <div>
              <label class="block text-sm mb-1">Curso <span class="text-red-500">*</span></label>
              <Dropdown
                v-model="filtroMatCapCurso"
                :options="cursoOpts"
                option-label="label"
                placeholder="Elegir curso"
                class="w-full"
                filter
                show-clear
                :disabled="!filtroMatCapMateria"
                @change="onFiltroMatCapCursoChange"
              />
            </div>
            <div>
              <label class="block text-sm mb-1">Capítulo <span class="text-red-500">*</span></label>
              <Dropdown
                v-model="filtroMatCapCapitulo"
                :options="capituloOptsMatCap"
                option-label="label"
                :placeholder="capituloMatCapPlaceholder"
                class="w-full"
                filter
                show-clear
                :disabled="!filtroMatCapMateria || !filtroMatCapCurso"
                :loading="loadingCapitulosMatCap"
              />
              <p v-if="filtroMatCapMateria && filtroMatCapCurso" class="text-xs text-500 mt-1 mb-0">
                {{ capitulosMatCap.length }} capítulo(s) en esta materia y nivel
              </p>
            </div>
            <div class="flex flex-wrap align-items-end gap-2">
              <ButtonPrime label="Buscar" icon="pi pi-search" :loading="loadingMatCap" @click="cargarMaterialesCap" />
              <ButtonPrime label="Limpiar" icon="pi pi-filter-slash" class="p-button-outlined" @click="limpiarFiltroMaterialesCap" />
            </div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3 p-3 border-round surface-border border-1">
            <div class="md:col-span-2">
              <label class="block text-sm mb-1">Título del material nuevo</label>
              <InputText v-model="nuevoMatCapTitulo" class="w-full" placeholder="Ej: Guía de apoyo" :disabled="!filtroMatCapCapitulo" />
            </div>
            <div class="flex align-items-end gap-2">
              <input ref="nuevoMatCapFile" type="file" accept="image/jpeg,image/png,image/webp,image/gif" class="hidden" @change="crearMaterialCapituloConImagen" />
              <ButtonPrime
                label="Crear + subir imagen"
                icon="pi pi-plus"
                :loading="creandoMatCap"
                :disabled="!filtroMatCapCapitulo"
                @click="nuevoMatCapFile?.click()"
              />
            </div>
          </div>
          <EstudyooLoadingBanner
            :visible="importandoMatCap || descargandoPlantillaMat"
            :message="importandoMatCap ? 'Importando materiales…' : 'Descargando plantilla…'"
          />
          <div class="flex flex-wrap gap-2 mb-3">
            <ButtonPrime label="Plantilla materiales" icon="pi pi-download" class="p-button-outlined" :loading="descargandoPlantillaMat" @click="bajarPlantillaMateriales" />
            <FileUpload
              mode="basic"
              choose-label="Importar materiales Excel"
              accept=".xlsx,.xls"
              :auto="true"
              custom-upload
              :disabled="importandoMatCap"
              @uploader="importarMaterialesExcel"
            />
          </div>
          <p v-if="!materialesCapTabReady" class="text-sm text-500 mb-3">
            Selecciona materia, curso y capítulo; luego pulsa <strong>Buscar</strong>.
          </p>
          <DataTable :value="materialesCap" :loading="loadingMatCap" class="p-datatable-sm" empty-message="Sin materiales para los filtros">
            <Column field="titulo" header="Título" />
            <Column header="Materia">
              <template #body>{{ filtroMatCapMateria?.nombre || '—' }}</template>
            </Column>
            <Column header="Curso">
              <template #body>{{ filtroMatCapCurso ? labelCursoRaw(filtroMatCapCurso) : '—' }}</template>
            </Column>
            <Column header="Capítulo">
              <template #body="{ data }">{{ labelCapituloId(data.capitulo_id) }}</template>
            </Column>
            <Column field="tipo" header="Tipo" />
            <Column header="Vista previa">
              <template #body="{ data }">
                <img v-if="data.url" :src="data.url" alt="" class="mat-thumb" />
                <span v-else class="text-500 text-xs">Sin imagen</span>
              </template>
            </Column>
            <Column header="Subir / reemplazar">
              <template #body="{ data }">
                <input
                  :ref="(el) => setFileRef('cap', data.id, el)"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  class="hidden"
                  @change="(e) => onSubirImagen(e, 'material_capitulo', data)"
                />
                <ButtonPrime
                  label="Imagen"
                  icon="pi pi-upload"
                  class="p-button-sm p-button-outlined"
                  :loading="uploadingKey === `cap-${data.id}`"
                  @click="triggerFile('cap', data.id)"
                />
              </template>
            </Column>
            <Column header="Editar" style="width: 5rem">
              <template #body="{ data }">
                <ButtonPrime icon="pi pi-pencil" class="p-button-text p-button-sm" @click="abrirEditar('material_cap', data)" />
              </template>
            </Column>
          </DataTable>
        </TabPanel>
        <TabPanel header="Asignaciones">
          <div class="p-4 border-round surface-border border-1 text-center">
            <i class="pi pi-share-alt text-4xl text-primary mb-3" />
            <h4 class="text-lg font-semibold mt-0 mb-2">Asignar actividades por curso</h4>
            <p class="text-sm text-600 mb-4 max-w-md mx-auto">
              Publica actividades al catálogo del alumno por <strong>materia</strong>, <strong>nivel</strong>,
              <strong>curso</strong> y <strong>capítulo</strong>. Esa gestión está en el hub dedicado.
            </p>
            <ButtonPrime
              label="Ir al hub de asignaciones"
              icon="pi pi-arrow-right"
              @click="router.push({ name: 'estudyoo-asignaciones' })"
            />
          </div>
        </TabPanel>
        <TabPanel header="Tipos">
          <div class="flex flex-wrap gap-2 mb-3">
            <ButtonPrime label="Recargar" icon="pi pi-refresh" :loading="loadingTipos" @click="() => cargarTipos(true)" />
          </div>
          <DataTable :value="tipos" :loading="loadingTipos" class="p-datatable-sm">
            <Column field="nombre" header="Nombre" />
          </DataTable>
        </TabPanel>
        <TabPanel header="Materias / Capítulos">
          <div class="flex flex-wrap gap-2 mb-3">
            <ButtonPrime label="Recargar" icon="pi pi-refresh" :loading="loadingCat" @click="() => cargarTab(TAB.CATALOG, true)" />
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <DataTable :value="materias" :loading="loadingCat" class="p-datatable-sm">
              <Column field="nombre" header="Materia" />
              <Column header="Editar" style="width: 5rem">
                <template #body="{ data }">
                  <ButtonPrime icon="pi pi-pencil" class="p-button-text p-button-sm" @click="abrirEditar('materia', data)" />
                </template>
              </Column>
            </DataTable>
            <DataTable :value="capitulos" :loading="loadingCat" class="p-datatable-sm">
              <Column field="nombre" header="Capítulo" />
              <Column header="Materia">
                <template #body="{ data }">{{ labelMateriaDeCapitulo(data) }}</template>
              </Column>
              <Column header="Nivel (curso)">
                <template #body="{ data }">{{ labelNivel(data.nivel_simce) }}</template>
              </Column>
              <Column header="Editar" style="width: 5rem">
                <template #body="{ data }">
                  <ButtonPrime icon="pi pi-pencil" class="p-button-text p-button-sm" @click="abrirEditarCapitulo(data)" />
                </template>
              </Column>
            </DataTable>
          </div>
          <p class="text-xs text-500 mt-2">
            Los capítulos deben existir (vía <strong>Sincronizar desde legacy</strong>) antes de importar actividades por <code>capitulo_codigo</code>.
          </p>
        </TabPanel>
      </TabView>

      <ActividadEstudyooEditor
        ref="editorRef"
        :ejes="ejes"
        :materias="materias"
        :cursos="cursoOptsEditor"
        :casas="casas"
        @saved="onEditGuardado"
      />
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import ButtonPrime from 'primevue/button';
import FileUpload from 'primevue/fileupload';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';
import TagPrime from 'primevue/tag';
import DjangoAuthGate from '@/components/estudyoo/DjangoAuthGate.vue';
import EstudyooLoadingBanner from '@/components/estudyoo/EstudyooLoadingBanner.vue';
import ActividadEstudyooEditor from '@/components/estudyoo/ActividadEstudyooEditor.vue';
import ActividadManualForm from '@/components/estudyoo/ActividadManualForm.vue';
import { ActividadesApi, descargarBlob } from '@/services/EstudyooAdminApi';
import { isDjangoAdminAuthenticated, handleDjangoApiError } from '@/services/djangoAdminAuth';
import { compararCursos, labelCurso as labelCursoObj, labelNivel, NIVEL_ORDEN } from '@/utils/nivelesEscolares';

const router = useRouter();
const toast = useToast();
const djangoOk = ref(isDjangoAdminAuthenticated());
const actividades = ref([]);
const preguntas = ref([]);
const materialesAct = ref([]);
const materialesCap = ref([]);
const alternativas = ref([]);
const cursos = ref([]);
const casas = ref([]);
const tipos = ref([]);
const materias = ref([]);
const capitulos = ref([]);
const ejes = ref([]);
const editorRef = ref(null);
const manualFormRef = ref(null);
const quitandoAsigId = ref(null);

const TAB = {
  ACTIVIDADES: 0,
  MANUAL: 1,
  PREGUNTAS: 2,
  MAT_ACT: 3,
  MAT_CAP: 4,
  ASIGNACIONES: 5,
  TIPOS: 6,
  CATALOG: 7,
};

const activeTabIndex = ref(TAB.ACTIVIDADES);
const loading = ref(false);
const loadingPreg = ref(false);
const loadingMat = ref(false);
const loadingMatCap = ref(false);
const loadingAlt = ref(false);
const loadingCat = ref(false);
const loadingTipos = ref(false);
const fileInputs = ref({});
const uploadingKey = ref('');
const nuevoMatCapTitulo = ref('');
const nuevoMatCapFile = ref(null);
const creandoMatCap = ref(false);
const nuevoMatActTitulo = ref('');
const nuevoMatActFile = ref(null);
const creandoMatAct = ref(false);
const descargandoPlantilla = ref(false);
const importando = ref(false);
const descargandoPlantillaMat = ref(false);
const importandoMat = ref(false);
const importandoMatCap = ref(false);
const filtroMateria = ref(null);
const filtroCurso = ref(null);
const filtroNivel = ref(null);
const filtroCapitulo = ref(null);
const capitulosActTab = ref([]);
const loadingCapitulosAct = ref(false);
const catalogLoading = ref(false);
const catalogReady = ref(false);
const resumenPanel = ref(null);
let catalogBootstrapDone = false;
let bootstrapInFlight = false;
const filtroPregActividad = ref(null);
const filtroMatActCurso = ref(null);
const filtroMatActCapitulo = ref(null);
const filtroMatActActividad = ref(null);
const filtroMatCapMateria = ref(null);
const filtroMatCapCurso = ref(null);
const filtroMatCapCapitulo = ref(null);
const capitulosMatCap = ref([]);
const loadingCapitulosMatCap = ref(false);
const actividadesBuscadas = ref(false);
const asignacionesCurso = ref([]);
const preguntasTabReady = ref(false);
const materialesActTabReady = ref(false);
const materialesCapTabReady = ref(false);

const catalogFlags = {
  capitulos: false,
  materias: false,
  ejes: false,
  cursos: false,
  casas: false,
  tipos: false,
  actividadesAsig: false,
};

const tabsLoaded = ref(new Set());

function onDjangoAuth(ok) {
  djangoOk.value = !!ok;
  if (!ok) {
    catalogBootstrapDone = false;
    tabsLoaded.value = new Set();
    actividadesBuscadas.value = false;
    catalogReady.value = false;
    resumenPanel.value = null;
    preguntasTabReady.value = false;
    materialesActTabReady.value = false;
    materialesCapTabReady.value = false;
    Object.keys(catalogFlags).forEach((k) => { catalogFlags[k] = false; });
    return;
  }
  bootstrapCatalog();
}

async function bootstrapCatalog(force = false) {
  if (!djangoOk.value) return;
  if (bootstrapInFlight) return;
  if (!force && catalogBootstrapDone) return;
  bootstrapInFlight = true;
  catalogLoading.value = true;
  if (force) {
    catalogFlags.cursos = false;
    catalogFlags.materias = false;
    catalogFlags.tipos = false;
    catalogBootstrapDone = false;
  }
  try {
    await Promise.all([
      cargarCursos(force),
      cargarMateriasCatalog(force),
      cargarTipos(force),
      cargarEjesCatalog(force),
    ]);
    await cargarResumenPanel();
    catalogBootstrapDone = true;
    catalogReady.value = true;
    tabsLoaded.value.add(TAB.ACTIVIDADES);
    if (!actividadesBuscadas.value) {
      await aplicarFiltroActividades();
    }
  } catch (e) {
    catchDjangoApi(e, 'Catálogo base');
  } finally {
    catalogLoading.value = false;
    bootstrapInFlight = false;
  }
}

async function cargarResumenPanel() {
  try {
    resumenPanel.value = await ActividadesApi.getResumen();
  } catch {
    resumenPanel.value = null;
  }
}

async function onTabChange(ev) {
  if (!djangoOk.value) return;
  await cargarTab(ev.index);
}

async function cargarTab(index, force = false) {
  if (!djangoOk.value) return;
  if (!force && tabsLoaded.value.has(index)) return;

  switch (index) {
    case TAB.ACTIVIDADES:
      if (!catalogBootstrapDone) await bootstrapCatalog();
      break;
    case TAB.MANUAL:
      if (!catalogBootstrapDone) await bootstrapCatalog();
      break;
    case TAB.PREGUNTAS:
      break;
    case TAB.MAT_ACT:
      if (!catalogBootstrapDone) await bootstrapCatalog();
      break;
    case TAB.MAT_CAP:
      if (!catalogBootstrapDone) await bootstrapCatalog();
      break;
    case TAB.ASIGNACIONES:
      break;
    case TAB.TIPOS:
      await cargarTipos(force);
      break;
    case TAB.CATALOG:
      await cargarMateriasCatalog(force);
      await cargarEjesCatalog(force);
      await cargarCapitulosCatalog(force);
      if (force) {
        catalogFlags.materias = true;
        catalogFlags.capitulos = true;
        catalogFlags.ejes = true;
      }
      break;
    default:
      break;
  }
  tabsLoaded.value.add(index);
}

const mapMaterias = computed(() => Object.fromEntries(materias.value.map((m) => [m.id, m])));
const mapTipos = computed(() => Object.fromEntries(tipos.value.map((t) => [t.id, t])));

const overviewCards = computed(() => {
  const c = resumenPanel.value?.conteos || {};
  const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('es-CL'));
  return [
    { key: 'act', label: 'Actividades', value: fmt(c.actividades) },
    { key: 'asig', label: 'Asignaciones activas', value: fmt(c.asignaciones_activas) },
    { key: 'cap', label: 'Capítulos', value: fmt(c.capitulos) },
    { key: 'cap-sn', label: 'Capítulos sin nivel', value: fmt(c.capitulos_sin_nivel), warn: (c.capitulos_sin_nivel || 0) > 0 },
    { key: 'preg', label: 'Preguntas', value: fmt(c.preguntas_actividad) },
    { key: 'mat', label: 'Materiales cap.', value: fmt(c.materiales_capitulo) },
  ];
});

const overviewAlerts = computed(() => {
  const c = resumenPanel.value?.conteos || {};
  const msgs = [];
  if ((c.capitulos_sin_nivel || 0) > 0) {
    msgs.push({
      severity: 'bg-orange-50 text-orange-800 border-1 border-orange-200',
      icon: 'pi-exclamation-triangle',
      text: `${c.capitulos_sin_nivel} capítulo(s) sin nivel curricular — revisa sync o el mapa de capítulos.`,
    });
  }
  if ((c.actividades || 0) > 0 && (c.asignaciones_activas || 0) === 0) {
    msgs.push({
      severity: 'bg-yellow-50 text-yellow-900 border-1 border-yellow-200',
      icon: 'pi-info-circle',
      text: 'Hay actividades pero ninguna asignación activa — los alumnos no las verán hasta asignar por curso.',
    });
  }
  if ((c.actividades || 0) === 0) {
    msgs.push({
      severity: 'bg-blue-50 text-blue-900 border-1 border-blue-200',
      icon: 'pi-info-circle',
      text: 'Aún no hay actividades. Importa Excel o usa generación con IA.',
    });
  }
  return msgs;
});

const capituloOptsActTab = computed(() =>
  capitulosActTab.value
    .map((c) => ({
      ...c,
      label: labelCapitulo(c, filtroMateria.value?.nombre),
    }))
    .sort((a, b) => (a.orden || 0) - (b.orden || 0) || a.nombre.localeCompare(b.nombre)),
);

const capituloActPlaceholder = computed(() => {
  if (!filtroMateria.value) return 'Opcional — elige materia';
  if (loadingCapitulosAct.value) return 'Cargando capítulos…';
  if (!capitulosActTab.value.length) return 'Sin capítulos (sync o mapa)';
  return 'Todos o uno específico';
});

const actividadStats = computed(() => {
  if (!actividadesBuscadas.value) return { total: 0, asignadas: 0, pendientes: 0 };
  const total = actividades.value.length;
  if (!filtroCurso.value?.id) return { total, asignadas: 0, pendientes: 0 };
  const asignadas = actividades.value.filter((a) => estaAsignadaAlCurso(a.id)).length;
  return { total, asignadas, pendientes: total - asignadas };
});

const nivelActivoFiltro = computed(
  () => filtroNivel.value || filtroCurso.value?.nivel_simce || '',
);

function labelCursoSeccion(c) {
  if (!c) return '—';
  const letra = c.letra || '—';
  return c.nombre_interno ? `${letra} · ${c.nombre_interno}` : letra;
}

const capituloOpts = computed(() =>
  capitulos.value
    .map((c) => ({
      ...c,
      label: labelCapitulo(c),
    }))
    .sort((a, b) => (a.orden || 0) - (b.orden || 0) || a.nombre.localeCompare(b.nombre)),
);

const nivelOptsActividades = computed(() => {
  const disponibles = new Set(cursos.value.map((c) => c.nivel_simce).filter(Boolean));
  const fromCursos = NIVEL_ORDEN.filter((n) => disponibles.has(n)).map((value) => ({
    value,
    label: labelNivel(value),
  }));
  return fromCursos.length
    ? fromCursos
    : NIVEL_ORDEN.map((value) => ({ value, label: labelNivel(value) }));
});

const cursoOptsActividades = computed(() => {
  const base = [...cursos.value].sort(compararCursos);
  const filtered = filtroNivel.value
    ? base.filter((c) => c.nivel_simce === filtroNivel.value)
    : base;
  return filtered.map((c) => ({ ...c, label: labelCursoSeccion(c) }));
});

const capituloOptsMatAct = computed(() => {
  const nivel = filtroMatActCurso.value?.nivel_simce;
  const base = capituloOpts.value;
  if (!nivel) return base;
  return base.filter((c) => !c.nivel_simce || c.nivel_simce === nivel);
});

const actividadOptsMatAct = computed(() => {
  let list = actividades.value;
  if (filtroMatActCapitulo.value?.id) {
    list = list.filter((a) => a.capitulo_id === filtroMatActCapitulo.value.id);
  } else if (filtroMatActCurso.value?.nivel_simce) {
    const nivel = filtroMatActCurso.value.nivel_simce;
    list = list.filter((a) => {
      const cap = mapCapitulos.value[a.capitulo_id];
      return !cap?.nivel_simce || cap.nivel_simce === nivel;
    });
  }
  return list.map((a) => {
    const cap = mapCapitulos.value[a.capitulo_id];
    return {
      ...a,
      label: `${a.titulo}${cap?.nombre ? ` · ${cap.nombre}` : ''}`,
    };
  });
});

const capituloOptsMatCap = computed(() =>
  capitulosMatCap.value
    .map((c) => ({
      ...c,
      label: labelCapitulo(c, filtroMatCapMateria.value?.nombre),
    }))
    .sort((a, b) => (a.orden || 0) - (b.orden || 0) || a.nombre.localeCompare(b.nombre)),
);

const capituloMatCapPlaceholder = computed(() => {
  if (!filtroMatCapMateria.value) return 'Primero elige materia';
  if (!filtroMatCapCurso.value) return 'Luego elige curso';
  if (loadingCapitulosMatCap.value) return 'Cargando capítulos…';
  if (!capitulosMatCap.value.length) return 'Sin capítulos para esta combinación';
  return 'Elegir capítulo';
});
const mapCapitulos = computed(() => Object.fromEntries(capitulos.value.map((c) => [c.id, c])));
const mapActividades = computed(() => Object.fromEntries(actividades.value.map((a) => [a.id, a])));
const mapEjes = computed(() => Object.fromEntries(ejes.value.map((e) => [e.id, e])));

function labelCursoRaw(c) {
  return labelCursoObj(c);
}

function onFiltroMateriaActChange() {
  filtroNivel.value = null;
  filtroCurso.value = null;
  filtroCapitulo.value = null;
  capitulosActTab.value = [];
  if (filtroMateria.value?.id) {
    cargarCapitulosActTab();
  }
}

function onFiltroNivelActChange() {
  filtroCurso.value = null;
  filtroCapitulo.value = null;
  actividadesBuscadas.value = false;
  actividades.value = [];
  cargarCapitulosActTab();
}

async function cargarCapitulosActTab() {
  filtroCapitulo.value = null;
  capitulosActTab.value = [];
  if (!filtroMateria.value?.id) return;
  loadingCapitulosAct.value = true;
  try {
    const params = { materia_id: filtroMateria.value.id };
    if (filtroNivel.value) params.nivel_simce = filtroNivel.value;
    capitulosActTab.value = await ActividadesApi.listCapitulos(params);
    mergeCapitulosIntoCatalog(capitulosActTab.value);
  } catch (e) {
    catchDjangoApi(e, 'Capítulos');
  } finally {
    loadingCapitulosAct.value = false;
  }
}

const cursoOpts = computed(() =>
  [...cursos.value]
    .sort(compararCursos)
    .map((c) => ({ ...c, label: labelCursoRaw(c) })),
);

const cursoOptsEditor = computed(() => cursoOpts.value);

const actividadOptsPreguntas = computed(() =>
  actividades.value.map((a) => {
    const cap = mapCapitulos.value[a.capitulo_id];
    return {
      ...a,
      label: `${a.titulo} · ${cap?.nombre || '—'}`,
    };
  }),
);

function labelActividad(id) {
  const a = mapActividades.value[id];
  if (!a) return id?.slice?.(0, 8) || '—';
  const cap = mapCapitulos.value[a.capitulo_id];
  return `${a.titulo}${cap?.nombre ? ` (${cap.nombre})` : ''}`;
}

function labelCapituloMaterial(row) {
  const nombre = row.capitulo_nombre;
  if (nombre) {
    const mat = row.materia_nombre || '';
    const nivel = row.nivel_simce ? ` · ${labelNivel(row.nivel_simce)}` : '';
    return mat ? `${mat} · ${nombre}${nivel}` : `${nombre}${nivel}`;
  }
  const act = mapActividades.value[row.actividad_id];
  const cap = act && mapCapitulos.value[act.capitulo_id];
  if (!cap) return '—';
  return labelCapitulo(cap);
}

function labelActividadMaterial(row) {
  if (row.actividad_titulo) return row.actividad_titulo;
  return labelActividad(row.actividad_id);
}

function labelCursoMaterial(row) {
  if (filtroMatActCurso.value) return labelCursoRaw(filtroMatActCurso.value);
  const cursos = row.cursos_asignados || [];
  if (cursos.length === 1) return labelCursoObj(cursos[0]);
  if (cursos.length > 1) {
    return cursos.map((c) => labelCursoObj(c)).join(', ');
  }
  if (row.nivel_simce) return labelNivel(row.nivel_simce);
  const act = mapActividades.value[row.actividad_id];
  const cap = act && mapCapitulos.value[act.capitulo_id];
  if (cap?.nivel_simce) return labelNivel(cap.nivel_simce);
  return '—';
}

function onFiltroMatActCursoChange() {
  const nivel = filtroMatActCurso.value?.nivel_simce;
  if (
    filtroMatActCapitulo.value?.nivel_simce
    && nivel
    && filtroMatActCapitulo.value.nivel_simce !== nivel
  ) {
    filtroMatActCapitulo.value = null;
  }
  filtroMatActActividad.value = null;
}

function onFiltroMatActCapituloChange() {
  filtroMatActActividad.value = null;
  const cap = filtroMatActCapitulo.value;
  if (cap?.nivel_simce && !filtroMatActCurso.value) {
    const match = cursoOpts.value.filter((c) => c.nivel_simce === cap.nivel_simce);
    if (match.length === 1) {
      filtroMatActCurso.value = match[0];
    }
  }
}

function limpiarFiltroMaterialesAct() {
  filtroMatActCurso.value = null;
  filtroMatActCapitulo.value = null;
  filtroMatActActividad.value = null;
  materialesAct.value = [];
  materialesActTabReady.value = false;
}

function labelCapituloId(id) {
  const cap = mapCapitulos.value[id] || capitulosMatCap.value.find((c) => c.id === id);
  if (!cap) return id?.slice?.(0, 8) || '—';
  return labelCapitulo(cap, filtroMatCapMateria.value?.nombre);
}

async function cargarCapitulosMatCap() {
  filtroMatCapCapitulo.value = null;
  capitulosMatCap.value = [];
  if (!filtroMatCapMateria.value?.id || !filtroMatCapCurso.value?.nivel_simce) return;
  loadingCapitulosMatCap.value = true;
  try {
    capitulosMatCap.value = await ActividadesApi.listCapitulos({
      materia_id: filtroMatCapMateria.value.id,
      nivel_simce: filtroMatCapCurso.value.nivel_simce,
    });
  } catch (e) {
    catchDjangoApi(e, 'Capítulos');
  } finally {
    loadingCapitulosMatCap.value = false;
  }
}

function onFiltroMatCapMateriaChange() {
  filtroMatCapCapitulo.value = null;
  materialesCap.value = [];
  materialesCapTabReady.value = false;
  if (filtroMatCapMateria.value && filtroMatCapCurso.value) {
    cargarCapitulosMatCap();
  } else {
    capitulosMatCap.value = [];
  }
}

function onFiltroMatCapCursoChange() {
  filtroMatCapCapitulo.value = null;
  materialesCap.value = [];
  materialesCapTabReady.value = false;
  if (filtroMatCapMateria.value && filtroMatCapCurso.value) {
    cargarCapitulosMatCap();
  } else {
    capitulosMatCap.value = [];
  }
}

function limpiarFiltroMaterialesCap() {
  filtroMatCapMateria.value = null;
  filtroMatCapCurso.value = null;
  filtroMatCapCapitulo.value = null;
  capitulosMatCap.value = [];
  materialesCap.value = [];
  materialesCapTabReady.value = false;
}

function estaAsignadaAlCurso(actividadId) {
  if (!filtroCurso.value?.id) return false;
  return asignacionesCurso.value.some(
    (a) => a.actividad_id === actividadId && a.curso_id === filtroCurso.value.id,
  );
}

function asignacionActividadEnCurso(actividadId) {
  if (!filtroCurso.value?.id) return null;
  return asignacionesCurso.value.find(
    (a) => a.actividad_id === actividadId && a.curso_id === filtroCurso.value.id,
  ) || null;
}

async function quitarAsignacionActividad(actividad) {
  const asig = asignacionActividadEnCurso(actividad.id);
  if (!asig?.id) return;
  const cursoLbl = labelCursoRaw(filtroCurso.value);
  if (!window.confirm(`¿Quitar la asignación de «${actividad.titulo}» del curso ${cursoLbl}?`)) return;
  quitandoAsigId.value = actividad.id;
  try {
    await ActividadesApi.deleteAsignacion(asig.id);
    toast.add({
      severity: 'info',
      summary: 'Asignación eliminada',
      detail: 'La actividad sigue en el catálogo; ya no está publicada a ese curso.',
      life: 5000,
    });
    catalogFlags.actividadesAsig = false;
    await aplicarFiltroActividades();
  } catch (e) {
    catchDjangoApi(e, 'Quitar asignación');
  } finally {
    quitandoAsigId.value = null;
  }
}

function onFiltroCursoChange() {
  if (filtroCurso.value?.nivel_simce && filtroCurso.value.nivel_simce !== filtroNivel.value) {
    filtroNivel.value = filtroCurso.value.nivel_simce;
  }
  if (!filtroCapitulo.value || !filtroCurso.value) return;
  const cap = filtroCapitulo.value;
  const nivel = nivelActivoFiltro.value;
  if (cap.nivel_simce && nivel && cap.nivel_simce !== nivel) {
    filtroCapitulo.value = null;
  }
}

function labelTipo(tipoId) {
  const t = mapTipos.value[tipoId];
  return t ? (t.nombre || t.codigo) : '—';
}

function labelCapitulo(cap, materiaNombre = '') {
  if (!cap) return '—';
  const mat = materiaNombre || labelMateriaDeCapitulo(cap);
  const nivel = cap.nivel_simce
    ? labelNivel(cap.nivel_simce)
    : (cap.nivel_simce === '' || cap.nivel_simce == null ? 'sin nivel' : '');
  const titulo = cap.nombre || '—';
  if (mat && mat !== '—') {
    return nivel ? `${mat} · ${titulo} · ${nivel}` : `${mat} · ${titulo}`;
  }
  return nivel ? `${titulo} · ${nivel}` : titulo;
}

function labelMateriaDeCapitulo(cap) {
  if (!cap) return '—';
  const eje = mapEjes.value[cap.eje_id || cap.eje];
  const fromEje = eje?.materia_id && mapMaterias.value[eje.materia_id]?.nombre;
  if (fromEje) return fromEje;
  if (cap.materia_id && mapMaterias.value[cap.materia_id]?.nombre) {
    return mapMaterias.value[cap.materia_id].nombre;
  }
  return '—';
}

function abrirEditar(entity, row) {
  editorRef.value?.open(entity, row);
}

async function abrirEditarCapitulo(row) {
  await cargarEjesCatalog();
  await cargarMateriasCatalog();
  abrirEditar('capitulo', row);
}

function onEditGuardado({ entity, row }) {
  const lists = {
    actividad: actividades,
    pregunta: preguntas,
    alternativa: alternativas,
    material_act: materialesAct,
    material_cap: materialesCap,
    materia: materias,
    capitulo: capitulos,
  };
  const list = lists[entity];
  if (list) {
    const i = list.value.findIndex((x) => x.id === row.id);
    if (i >= 0) list.value[i] = { ...list.value[i], ...row };
    else if (entity === 'materia' || entity === 'capitulo') list.value.push(row);
  }
  toast.add({ severity: 'success', summary: 'Guardado', detail: 'Cambios aplicados.', life: 3000 });
}

const actividadesAgrupadasPorCapitulo = computed(() => {
  if (!actividadesBuscadas.value || !actividades.value.length) return [];

  function capParaActividad(a) {
    const cap = mapCapitulos.value[a.capitulo_id];
    if (cap) return cap;
    return {
      id: a.capitulo_id || `sin-cap-${a.id}`,
      nombre: a.capitulo_nombre || 'Sin capítulo',
      orden: 9999,
    };
  }

  const byCap = new Map();
  for (const a of actividades.value) {
    const cap = capParaActividad(a);
    const key = cap.id;
    if (!byCap.has(key)) {
      byCap.set(key, { capitulo: cap, actividades: [] });
    }
    byCap.get(key).actividades.push({
      ...a,
      asignada: filtroCurso.value ? estaAsignadaAlCurso(a.id) : null,
    });
  }

  return [...byCap.values()]
    .map((g) => {
      const matLabel = labelMateriaDeCapitulo(g.capitulo);
      return {
        ...g,
        capituloLabel: labelCapitulo(g.capitulo, matLabel !== '—' ? matLabel : filtroMateria.value?.nombre),
        cursoLabel: filtroCurso.value ? labelCursoRaw(filtroCurso.value) : '',
      };
    })
    .sort((a, b) => (a.capitulo.orden || 0) - (b.capitulo.orden || 0) || a.capituloLabel.localeCompare(b.capituloLabel));
});

function onDjangoUnauthorized() {
  djangoOk.value = false;
  tabsLoaded.value = new Set();
}

function catchDjangoApi(e, context = '') {
  if (handleDjangoApiError(e, { toast, onUnauthorized: onDjangoUnauthorized })) return;
  toast.add({
    severity: 'error',
    summary: context || 'Error de API',
    detail: e.response?.data?.detail || e.message || 'Error desconocido',
    life: 6000,
  });
}

function mergeCapitulosIntoCatalog(caps) {
  const known = new Set(capitulos.value.map((c) => c.id));
  for (const cap of caps) {
    if (!known.has(cap.id)) {
      capitulos.value.push(cap);
      known.add(cap.id);
    }
  }
}

async function ensureCapitulosForActividades(lista) {
  const capIds = [...new Set(lista.map((a) => a.capitulo_id).filter(Boolean))];
  const missing = capIds.filter((id) => !mapCapitulos.value[id]);
  if (!missing.length) return;

  if (filtroMateria.value?.id) {
    const params = { materia_id: filtroMateria.value.id };
    if (filtroNivel.value) params.nivel_simce = filtroNivel.value;
    try {
      mergeCapitulosIntoCatalog(await ActividadesApi.listCapitulos(params));
    } catch (e) {
      catchDjangoApi(e, 'Capítulos');
    }
    return;
  }

  const materiaIds = [...new Set(lista.map((a) => a.materia_id).filter(Boolean))];
  for (const mid of materiaIds.slice(0, 8)) {
    try {
      mergeCapitulosIntoCatalog(await ActividadesApi.listCapitulos({ materia_id: mid }));
    } catch {
      /* continuar con otras materias */
    }
  }
}

async function aplicarFiltroActividades() {
  loading.value = true;
  try {
    await cargarTipos();
    const params = {};
    if (filtroMateria.value?.id) params.materia_id = filtroMateria.value.id;
    if (filtroCapitulo.value?.id) params.capitulo_id = filtroCapitulo.value.id;

    let lista = await ActividadesApi.listActividades(params);

    if (filtroNivel.value && !filtroCapitulo.value) {
      if (filtroMateria.value?.id && !capitulosActTab.value.length) {
        await cargarCapitulosActTab();
      }
      await ensureCapitulosForActividades(lista);
      lista = lista.filter((a) => {
        const cap = mapCapitulos.value[a.capitulo_id];
        if (!cap) return true;
        return !cap.nivel_simce || cap.nivel_simce === filtroNivel.value;
      });
    } else if (lista.length) {
      await ensureCapitulosForActividades(lista);
    }

    actividades.value = lista;
    if (filtroCurso.value?.id) {
      asignacionesCurso.value = await ActividadesApi.listAsignaciones({ curso_id: filtroCurso.value.id });
    } else {
      asignacionesCurso.value = [];
    }
    actividadesBuscadas.value = true;
  } catch (e) {
    catchDjangoApi(e, 'Actividades');
  } finally {
    loading.value = false;
  }
}

function limpiarFiltroActividades() {
  filtroMateria.value = null;
  filtroNivel.value = null;
  filtroCurso.value = null;
  filtroCapitulo.value = null;
  capitulosActTab.value = [];
  asignacionesCurso.value = [];
  aplicarFiltroActividades();
}

async function cargarActividades(params) {
  loading.value = true;
  try {
    actividades.value = await ActividadesApi.listActividades(params);
  } catch (e) {
    catchDjangoApi(e, 'Actividades');
  } finally {
    loading.value = false;
  }
}

async function cargarActividadesParaFiltro() {
  if (catalogFlags.actividadesAsig && actividades.value.length) return;
  if (!catalogFlags.capitulos) await cargarCapitulosCatalog();
  await cargarActividades();
  catalogFlags.actividadesAsig = true;
}

async function cargarActividadesParaFiltroMat() {
  if (!catalogFlags.capitulos) await cargarCapitulosCatalog();
  const params = {};
  if (filtroMatActCapitulo.value?.id) params.capitulo_id = filtroMatActCapitulo.value.id;
  if (!params.capitulo_id && !actividades.value.length) {
    await cargarActividades();
    return;
  }
  if (params.capitulo_id) {
    actividades.value = await ActividadesApi.listActividades(params);
  }
}

async function cargarPreguntas() {
  loadingPreg.value = true;
  try {
    const params = {};
    if (filtroPregActividad.value?.id) params.actividad_id = filtroPregActividad.value.id;
    preguntas.value = await ActividadesApi.listPreguntasActividad(
      Object.keys(params).length ? params : undefined,
    );
    preguntasTabReady.value = true;
  } catch (e) {
    catchDjangoApi(e, 'Preguntas');
  } finally {
    loadingPreg.value = false;
  }
}

async function cargarMaterialesAct() {
  loadingMat.value = true;
  try {
    if (!catalogFlags.capitulos) await cargarCapitulosCatalog();
    if (!catalogFlags.cursos) await cargarCursos();
    if (!actividades.value.length) {
      await aplicarFiltroActividades();
    }
    const params = {};
    if (filtroMatActActividad.value?.id) params.actividad_id = filtroMatActActividad.value.id;
    if (filtroMatActCapitulo.value?.id) params.capitulo_id = filtroMatActCapitulo.value.id;
    if (filtroMatActCurso.value?.id) params.curso_id = filtroMatActCurso.value.id;
    materialesAct.value = await ActividadesApi.listMaterialesActividad(
      Object.keys(params).length ? params : undefined,
    );
    materialesActTabReady.value = true;
  } catch (e) {
    catchDjangoApi(e, 'Materiales actividad');
  } finally {
    loadingMat.value = false;
  }
}

async function cargarMaterialesCap() {
  if (!filtroMatCapMateria.value?.id) {
    toast.add({ severity: 'warn', summary: 'Filtros', detail: 'Selecciona una materia.', life: 4000 });
    return;
  }
  if (!filtroMatCapCurso.value?.id) {
    toast.add({ severity: 'warn', summary: 'Filtros', detail: 'Selecciona un curso.', life: 4000 });
    return;
  }
  if (!filtroMatCapCapitulo.value?.id) {
    toast.add({ severity: 'warn', summary: 'Filtros', detail: 'Selecciona un capítulo.', life: 4000 });
    return;
  }
  loadingMatCap.value = true;
  try {
    materialesCap.value = await ActividadesApi.listMaterialesCapitulo({
      capitulo_id: filtroMatCapCapitulo.value.id,
    });
    materialesCapTabReady.value = true;
  } catch (e) {
    catchDjangoApi(e, 'Materiales capítulo');
  } finally {
    loadingMatCap.value = false;
  }
}

async function cargarAlternativas() {
  loadingAlt.value = true;
  try {
    alternativas.value = await ActividadesApi.listAlternativas();
  } catch (e) {
    catchDjangoApi(e, 'Alternativas');
  } finally {
    loadingAlt.value = false;
  }
}

function setFileRef(grupo, id, el) {
  if (el) fileInputs.value[`${grupo}-${id}`] = el;
}

function triggerFile(grupo, id) {
  fileInputs.value[`${grupo}-${id}`]?.click();
}

async function onSubirImagen(ev, entidad, row, campoUrl = 'url') {
  const file = ev.target.files?.[0];
  if (!file) return;
  const grupo = entidad === 'material_actividad' ? 'act' : entidad === 'material_capitulo' ? 'cap' : 'alt';
  uploadingKey.value = `${grupo}-${row.id}`;
  try {
    const r = await ActividadesApi.subirImagenMaterial(entidad, row.id, file);
    row[campoUrl] = r.url;
    if (entidad !== 'alternativa') row.tipo = 'imagen';
    toast.add({ severity: 'success', summary: 'Imagen', detail: 'Archivo subido correctamente.', life: 4000 });
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Error al subir',
      detail: e.response?.data?.detail || e.message,
      life: 6000,
    });
  } finally {
    uploadingKey.value = '';
    ev.target.value = '';
  }
}

async function crearMaterialCapituloConImagen(ev) {
  const file = ev.target.files?.[0];
  const cap = filtroMatCapCapitulo.value;
  if (!file || !cap?.id || !nuevoMatCapTitulo.value.trim()) {
    toast.add({ severity: 'warn', summary: 'Datos incompletos', detail: 'Indica materia, curso, capítulo, título e imagen.', life: 5000 });
    ev.target.value = '';
    return;
  }
  creandoMatCap.value = true;
  try {
    const mat = await ActividadesApi.crearMaterialCapitulo({
      capitulo_id: cap.id,
      titulo: nuevoMatCapTitulo.value.trim(),
      tipo: 'imagen',
      contenido: '',
      url: '',
      orden: 0,
      activo: true,
    });
    const r = await ActividadesApi.subirImagenMaterial('material_capitulo', mat.id, file);
    toast.add({ severity: 'success', summary: 'Material creado', detail: 'Imagen subida al capítulo.', life: 5000 });
    nuevoMatCapTitulo.value = '';
    await cargarMaterialesCap();
    const found = materialesCap.value.find((m) => m.id === mat.id);
    if (found) found.url = r.url;
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: e.response?.data?.detail || e.message,
      life: 6000,
    });
  } finally {
    creandoMatCap.value = false;
    ev.target.value = '';
  }
}

async function crearMaterialActividadConImagen(ev) {
  const file = ev.target.files?.[0];
  const act = filtroMatActActividad.value;
  if (!file || !act?.id || !nuevoMatActTitulo.value.trim()) {
    toast.add({
      severity: 'warn',
      summary: 'Datos incompletos',
      detail: 'Elige una actividad, indica título e imagen.',
      life: 5000,
    });
    ev.target.value = '';
    return;
  }
  creandoMatAct.value = true;
  try {
    const mat = await ActividadesApi.crearMaterialActividad({
      actividad_id: act.id,
      titulo: nuevoMatActTitulo.value.trim(),
      tipo: 'imagen',
      contenido: '',
      url: '',
      orden: 0,
      obligatorio: true,
    });
    await ActividadesApi.subirImagenMaterial('material_actividad', mat.id, file);
    toast.add({ severity: 'success', summary: 'Material creado', detail: 'Imagen subida a la actividad.', life: 5000 });
    nuevoMatActTitulo.value = '';
    await cargarMaterialesAct();
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: e.response?.data?.detail || e.message,
      life: 6000,
    });
  } finally {
    creandoMatAct.value = false;
    ev.target.value = '';
  }
}

async function cargarCapitulosCatalog(force = false) {
  if (!force && catalogFlags.capitulos) return;
  loadingCat.value = true;
  try {
    capitulos.value = await ActividadesApi.listCapitulos();
    catalogFlags.capitulos = true;
  } catch (e) {
    catchDjangoApi(e, 'Capítulos');
  } finally {
    loadingCat.value = false;
  }
}

async function cargarEjesCatalog(force = false) {
  if (!force && catalogFlags.ejes) return;
  loadingCat.value = true;
  try {
    ejes.value = await ActividadesApi.listEjes();
    catalogFlags.ejes = true;
  } catch (e) {
    catchDjangoApi(e, 'Ejes temáticos');
  } finally {
    loadingCat.value = false;
  }
}

async function cargarMateriasCatalog(force = false) {
  if (!force && catalogFlags.materias) return;
  loadingCat.value = true;
  try {
    materias.value = await ActividadesApi.listMaterias({}, { force });
    catalogFlags.materias = true;
  } catch (e) {
    catchDjangoApi(e, 'Materias');
  } finally {
    loadingCat.value = false;
  }
}

async function cargarTipos(force = false) {
  if (!force && catalogFlags.tipos) return;
  loadingTipos.value = true;
  try {
    tipos.value = await ActividadesApi.listTipos({}, { force });
    catalogFlags.tipos = true;
  } catch (e) {
    catchDjangoApi(e, 'Tipos');
  } finally {
    loadingTipos.value = false;
  }
}

async function cargarCursos(force = false) {
  if (!force && catalogFlags.cursos) return;
  try {
    cursos.value = await ActividadesApi.listCursos({}, { force });
    catalogFlags.cursos = true;
  } catch (e) {
    catchDjangoApi(e, 'Cursos');
  }
}

function irTabManual() {
  activeTabIndex.value = TAB.MANUAL;
  cargarTab(TAB.MANUAL);
  nextTick(() => manualFormRef.value?.resetForm?.(true));
}

function editarActividadManual(row) {
  activeTabIndex.value = TAB.MANUAL;
  cargarTab(TAB.MANUAL);
  nextTick(() => manualFormRef.value?.loadActividad(row));
}

function editarActividadDesdePregunta(preg) {
  const actividadId = preg?.actividad_id || preg?.actividad;
  if (!actividadId) {
    toast.add({
      severity: 'warn',
      summary: 'Pregunta',
      detail: 'No se encontró la actividad asociada a esta pregunta.',
      life: 4000,
    });
    return;
  }
  editarActividadManual({ id: actividadId });
}

async function onManualSaved() {
  catalogFlags.actividadesAsig = false;
  tabsLoaded.value.delete(TAB.PREGUNTAS);
  tabsLoaded.value.delete(TAB.MAT_ACT);
  tabsLoaded.value.delete(TAB.ASIGNACIONES);
  await cargarResumenPanel();
  if (actividadesBuscadas.value) {
    await aplicarFiltroActividades();
  }
}

async function onManualImported() {
  catalogFlags.actividadesAsig = false;
  tabsLoaded.value.delete(TAB.PREGUNTAS);
  tabsLoaded.value.delete(TAB.MAT_ACT);
  tabsLoaded.value.delete(TAB.MAT_CAP);
  await cargarResumenPanel();
  if (actividadesBuscadas.value) {
    await aplicarFiltroActividades();
  }
}

async function bajarPlantilla() {
  descargandoPlantilla.value = true;
  try {
    const res = await ActividadesApi.descargarPlantillaActividades();
    descargarBlob(res, 'plantilla-actividades-estudyoo.xlsx');
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Plantilla', detail: e.message, life: 5000 });
  } finally {
    descargandoPlantilla.value = false;
  }
}

async function importar(ev) {
  const file = ev.files[0];
  if (!file) return;
  importando.value = true;
  try {
    const r = await ActividadesApi.importarActividades(file);
    const a = r.actividades || {};
    const p = r.preguntas || {};
    const m = r.materiales || {};
    toast.add({
      severity: r.ok ? 'success' : 'warn',
      summary: 'Importación',
      detail: `Actividades: +${a.creadas || 0}/~${a.actualizadas || 0} · Preguntas: +${p.creadas || 0} · Materiales: +${m.creadas || 0}${r.errores?.length ? ` · ${r.errores.length} error(es)` : ''}`,
      life: 8000,
    });
    catalogFlags.actividadesAsig = false;
    tabsLoaded.value.delete(TAB.PREGUNTAS);
    tabsLoaded.value.delete(TAB.MAT_ACT);
    if (actividadesBuscadas.value) {
      await aplicarFiltroActividades();
    }
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: e.message, life: 5000 });
  } finally {
    importando.value = false;
    if (ev?.options?.clear) ev.options.clear();
  }
}

async function bajarPlantillaMateriales() {
  descargandoPlantillaMat.value = true;
  try {
    const res = await ActividadesApi.descargarPlantillaMateriales();
    descargarBlob(res, 'plantilla-materiales-estudyoo.xlsx');
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Plantilla materiales', detail: e.message, life: 5000 });
  } finally {
    descargandoPlantillaMat.value = false;
  }
}

async function importarMaterialesExcel(ev) {
  const file = ev.files?.[0];
  if (!file) return;
  importandoMat.value = true;
  importandoMatCap.value = true;
  try {
    const colegioId = filtroCurso.value?.colegio_id || filtroMatCapCurso.value?.colegio_id || filtroMateria.value?.colegio_id;
    const r = await ActividadesApi.importarMateriales(file, colegioId);
    const m = r.materiales || {};
    toast.add({
      severity: r.ok ? 'success' : 'warn',
      summary: 'Importación materiales',
      detail: `Materiales: +${m.creadas || 0}/~${m.actualizadas || 0}${r.errores?.length ? ` · ${r.errores.length} error(es)` : ''}`,
      life: 8000,
    });
    if (materialesActTabReady.value) await cargarMaterialesAct();
    if (materialesCapTabReady.value) await cargarMaterialesCap();
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: e.message, life: 5000 });
  } finally {
    importandoMat.value = false;
    importandoMatCap.value = false;
    ev?.options?.clear?.();
  }
}

onMounted(() => {
  if (djangoOk.value) bootstrapCatalog();
});
</script>

<style scoped>
.mat-thumb {
  max-width: 72px;
  max-height: 48px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid var(--surface-border);
}
.hidden {
  display: none;
}
.act-ia-banner {
  border: 1px solid #c4b5fd;
  background: linear-gradient(90deg, rgba(237, 233, 254, 0.5) 0%, transparent 70%);
}
.act-overview__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(7.5rem, 1fr));
  gap: 0.65rem;
}
.act-overview__stat {
  padding: 0.5rem 0.65rem;
  border-radius: 8px;
  background: var(--surface-ground);
  border: 1px solid var(--surface-border);
}
.act-overview__stat-value {
  display: block;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.2;
}
.act-overview__stat-label {
  font-size: 0.68rem;
  color: var(--text-color-secondary);
}
.act-mini-stat {
  min-width: 6.5rem;
}
.text-violet-500 {
  color: #7c3aed;
}
</style>
